import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, createContext, useContext } from "react";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { ContentProvider } from "@/context/ContentContext";
import { trackPageView } from "@/lib/analytics";

// Pages
import LandingPage from "@/pages/LandingPage";
import NotFoundPage from "@/pages/NotFoundPage";
import KVKKPage from "@/pages/KVKKPage";
import DogsPage from "@/pages/DogsPage";
import DogProfilePage from "@/pages/DogProfilePage";
import DashboardPage from "@/pages/DashboardPage";
import AdoptionFormPage from "@/pages/AdoptionFormPage";
import GuardianFormPage from "@/pages/GuardianFormPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import TransparencyPage from "@/pages/TransparencyPage";
import GuardianUpdatesPage from "@/pages/GuardianUpdatesPage";
import ApplicationSuccessPage from "@/pages/ApplicationSuccessPage";
import DonationSuccessPage from "@/pages/DonationSuccessPage";
import EventsPage from "@/pages/EventsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsentBanner from "@/components/CookieConsentBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Oturum çerezi API farklı bir host'ta (canlıda www → apex) servis edildiğinde
// yalnızca credentials gönderilen isteklerde iletiliyor → global olarak açıyoruz.
axios.defaults.withCredentials = true;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          setUser(response.data.user);
          // Clear hash and navigate
          window.history.replaceState(null, '', '/dashboard');
          navigate('/dashboard', { state: { user: response.data.user }, replace: true });
        } catch (error) {
          console.error("Auth error:", error);
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Giriş yapılıyor...</p>
      </div>
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Track SPA route changes (GA4 + Meta Pixel)
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Check URL fragment for session_id during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Halka açık route'lar — hem TR (/) hem EN (/en/) altında render edilir.
  // Admin ve Dashboard TR-only (EN prefix yok).
  const publicRoutes = [
    { path: "", element: <LandingPage /> },
    { path: "canlar", element: <DogsPage /> },
    { path: "can/:dogId", element: <DogProfilePage /> },
    { path: "sahiplen/:dogId", element: <AdoptionFormPage /> },
    { path: "koruyucu-aile/:dogId", element: <GuardianFormPage /> },
    { path: "seffaflik", element: <TransparencyPage /> },
    { path: "basvuru-tamamlandi/:applicationId", element: <ApplicationSuccessPage /> },
    { path: "bagis/tesekkurler", element: <DonationSuccessPage /> },
    { path: "guncellemeler/:dogId/:token", element: <GuardianUpdatesPage /> },
    { path: "etkinlikler", element: <EventsPage /> },
    { path: "etkinlikler/:eventId", element: <EventDetailPage /> },
    { path: "iletisim", element: <ContactPage /> },
    { path: "gizlilik", element: <PrivacyPage /> },
    { path: "kvkk", element: <KVKKPage /> },
  ];

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* TR (default) */}
        {publicRoutes.map(r => (
          <Route key={`tr-${r.path}`} path={r.path === "" ? "/" : `/${r.path}`} element={r.element} />
        ))}
        {/* EN — locale LanguageContext tarafından URL'den otomatik yakalanır */}
        {publicRoutes.map(r => (
          <Route key={`en-${r.path}`} path={r.path === "" ? "/en" : `/en/${r.path}`} element={r.element} />
        ))}
        {/* Admin — TR only, dil bağımsız */}
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/dashboard/*" element={<DashboardPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ContentProvider>
          <div className="min-h-screen bg-background">
            <AppRouter />
            <CookieConsentBanner />
            <Toaster position="top-right" />
          </div>
        </ContentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
